import AsyncStorage from '@react-native-async-storage/async-storage';
import { colorScheme as nativewindColorScheme } from 'nativewind';
import { Appearance, Platform } from 'react-native';
import { create } from 'zustand';
import { createJSONStorage, persist, type StateStorage } from 'zustand/middleware';

/** O que a pessoa escolheu em Perfil > Aparência. */
export type ThemePreference = 'system' | 'light' | 'dark';
/** O tema que de fato está na tela. */
export type ResolvedTheme = 'light' | 'dark';

/** Evita cache do createJSONStorage apontar para dummyStorage após SSR. */
const themeWebStorage: StateStorage = {
  getItem: (name) => {
    if (typeof window === 'undefined') return null;
    return window.localStorage.getItem(name);
  },
  setItem: (name, value) => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(name, value);
  },
  removeItem: (name) => {
    if (typeof window === 'undefined') return;
    window.localStorage.removeItem(name);
  },
};

/** Chave do localStorage também lida pelo script anti-flash do `+html.tsx`. */
export const THEME_STORAGE_KEY = 'theme-storage';

interface ThemeState {
  preference: ThemePreference;
  /** Tema efetivo (preferência resolvida contra o sistema). */
  colorScheme: ResolvedTheme;
  setPreference: (preference: ThemePreference) => void;
  _hasHydrated: boolean;
  setHasHydrated: (state: boolean) => void;
}

type ThemePersisted = Pick<ThemeState, 'preference'>;

const themeStorage =
  Platform.OS === 'web'
    ? createJSONStorage<ThemePersisted>(() => themeWebStorage)
    : createJSONStorage<ThemePersisted>(() => AsyncStorage);

function systemScheme(): ResolvedTheme {
  if (Platform.OS === 'web') {
    if (typeof window === 'undefined' || !window.matchMedia) return 'light';
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return Appearance.getColorScheme() === 'dark' ? 'dark' : 'light';
}

function resolve(preference: ThemePreference): ResolvedTheme {
  return preference === 'system' ? systemScheme() : preference;
}

/**
 * Aplica o tema no NativeWind (variáveis `.dark:root`) e, na web, na classe do
 * `<html>`. No nativo "sistema" devolve o controle ao aparelho; na web o
 * NativeWind não põe a classe sozinho no modo sistema, então resolvemos aqui.
 */
function applyTheme(preference: ThemePreference): ResolvedTheme {
  if (Platform.OS === 'web') {
    const resolved = resolve(preference);
    if (typeof document === 'undefined') return resolved;
    document.documentElement.classList.toggle('dark', resolved === 'dark');
    try {
      nativewindColorScheme.set(resolved);
    } catch {
      // Antes do stylesheet do NativeWind montar: a classe no <html> já basta.
    }
    return resolved;
  }

  // No nativo a escolha vira override do Appearance; lê o esquema depois dele.
  nativewindColorScheme.set(preference);
  return resolve(preference);
}

export const useThemeStore = create<ThemeState>()(
  persist(
    (set) => ({
      preference: 'system',
      colorScheme: resolve('system'),
      _hasHydrated: false,
      setHasHydrated: (state) => {
        set({ _hasHydrated: state });
      },
      setPreference: (preference) => {
        set({ preference, colorScheme: applyTheme(preference) });
      },
    }),
    {
      name: THEME_STORAGE_KEY,
      version: 1,
      storage: themeStorage,
      // v0 era o tema claro forçado ({ colorScheme, followSystem }): ninguém
      // escolheu nada ainda, então todo mundo começa seguindo o sistema.
      migrate: () => ({ preference: 'system' }) as ThemePersisted,
      partialize: (state) => ({ preference: state.preference }),
      onRehydrateStorage: () => (state) => {
        const preference = state?.preference ?? 'system';
        useThemeStore.setState({
          _hasHydrated: true,
          preference,
          colorScheme: applyTheme(preference),
        });
      },
    },
  ),
);

/** Mantém "Sistema" acompanhando o aparelho/navegador enquanto o app está aberto. */
function followSystemChanges() {
  const onChange = () => {
    const { preference } = useThemeStore.getState();
    if (preference !== 'system') return;
    useThemeStore.setState({ colorScheme: applyTheme('system') });
  };

  if (Platform.OS === 'web') {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', onChange);
    return;
  }
  Appearance.addChangeListener(onChange);
}

followSystemChanges();
